import Layout from "@/components/Layout";
import { motion } from "framer-motion";
import { Building2, Rocket, Shield, Users, Globe } from "lucide-react";
import { STATS } from "@/data/content";
import Testimonials from "@/components/home/Testimonials";
import ContactCTA from "@/components/home/ContactCTA";
import AnimatedVisual from "@/components/AnimatedVisual";

const openDemo = () => window.dispatchEvent(new CustomEvent("alphatech:open-demo"));

export default function About() {
  return (
    <Layout>
      <section className="relative pt-16 pb-24">
        <div className="absolute inset-0 hero-glow opacity-60" aria-hidden />
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="overline">About Alphatech</div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]" data-testid="about-title">
              An iGaming engineering company, built in <span className="text-gradient-blue">Dubai</span>.
            </h1>
            <p className="mt-5 text-gray-400 leading-relaxed max-w-2xl">
              Alphatech Labz FZ LLC builds white-label and turnkey iGaming platforms for licensed operators across the globe. From platform engineering to payment integrations, our team of 200+ engineers ships production-grade software that ships fast, scales globally, and holds up under real player traffic.
            </p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {STATS.map((s, i) => (
                <div key={s.label} className="p-5 rounded-2xl bg-[#0B1224]/70 border border-white/10" data-testid={`about-stat-${i}`}>
                  <div className="font-display text-3xl font-bold text-gradient-blue">{s.value}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-gray-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-square rounded-3xl overflow-hidden glass-strong">
              <AnimatedVisual variant="globe" className="absolute inset-0" label="Global iGaming presence"/>
              <div className="absolute inset-0 bg-gradient-to-tr from-[#050816] via-transparent to-transparent"/>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 bg-[#040611]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="overline">What We Stand For</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            Our operating principles.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { icon: Rocket, title: "Ship Fast", desc: "72-hour launch guarantee for white-label projects. No excuses." },
              { icon: Shield, title: "Trust First", desc: "AML, KYC, PCI-DSS and licensed jurisdictions — compliance is default." },
              { icon: Users, title: "Partner Long-term", desc: "We share risk with operators. Growth-based commercials, no lock-ins." },
              { icon: Globe, title: "Go Global", desc: "Multi-lingual, multi-currency, multi-crypto. Ready for any market." },
              { icon: Building2, title: "Enterprise Grade", desc: "99.99% uptime SLA on managed hosting with 24/7 pager rotation." },
              { icon: Rocket, title: "Product-led", desc: "Weekly releases across platform, bonus engine and CRM." },
            ].map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.04 }}
                data-testid={`about-principle-${i}`}
                className="p-6 rounded-2xl bg-[#0B1224]/70 border border-white/10"
              >
                <div className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-[#00D4FF]"/>
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold">{p.title}</h3>
                <p className="mt-1.5 text-sm text-gray-400 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <ContactCTA onOpenDemo={openDemo} />
    </Layout>
  );
}
